"use client";

import logger from "@/utils/logger";

import { useCallback, useEffect } from "react";
import { useAuth } from "@/context/auth-context";
import { useSignalR } from "@/contexts/SignalRContext";
import { useNotificationToast } from "@/components/ui/NotificationToastContainer";
import { useGlobalBanner } from "@/providers/GlobalBannerProvider";
import { audioManager } from "@/utils/audioManager";
import { useTripAnimation } from "@/contexts/TripAnimationContext";
import { useQueryClient } from "@tanstack/react-query";
import {
    NotificationMessages,
    NotificationTypes,
} from "@/utils/notificationMessages";

/**
 * Provider that handles notifications for trip issues.
 * Routes "Report Issue" critical notifications to full-width banner.
 * Routes "Default" standard notifications to toast (bottom-right).
 */
export function DeliveryIssueToastProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    logger.info("🍞🍞🍞 [DeliveryIssueToastProvider] COMPONENT RENDERING");

    const { showNotificationToast } = useNotificationToast();
    const { addBanner } = useGlobalBanner();
    const { triggerAnimation } = useTripAnimation();
    const queryClient = useQueryClient();

    logger.info("🍞 [DeliveryIssueToastProvider] About to call useSignalR()");
    const { registerNotificationHandler } = useSignalR();
    logger.info(
        "🍞 [DeliveryIssueToastProvider] useSignalR() succeeded, got registerNotificationHandler:",
        typeof registerNotificationHandler
    );

    const { userProfile, externalUserProfile, loginType } = useAuth();

    // Get current user email for driver-specific notifications
    const currentUserEmail =
        loginType === "msal" ? userProfile?.email : externalUserProfile?.email;

    logger.info(
        "🍞 [DeliveryIssueToastProvider] Initializing with email:",
        currentUserEmail
    );

    // Handler for notifications - routes to banner or toast based on notification type
    const handleNotification = useCallback(
        (notification: unknown) => {
            logger.info(
                "🍞🍞🍞 [DEBUG-DELIVERY] DeliveryIssueToastProvider handleNotification called!"
            );
            logger.info("🍞 [DEBUG-DELIVERY] Raw notification:", notification);

            logger.info(
                "🍞🍞🍞 [DeliveryIssueToastProvider] RAW NOTIFICATION RECEIVED:",
                JSON.stringify(notification, null, 2)
            );
            logger.info(
                "🍞 [DeliveryIssueToastProvider] Notification received (object):",
                notification
            );

            if (notification && typeof notification === "object") {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const notificationData = notification as any;
                let customData = notificationData.customData;

                // Parse customData if it's a JSON string (from database fetch)
                if (typeof customData === "string") {
                    try {
                        customData = JSON.parse(customData);
                    } catch (error) {
                        console.error("Failed to parse customData:", error);
                        customData = null;
                    }
                }

                logger.info(
                    "📦 [DeliveryIssueToastProvider] CustomData (parsed if needed):",
                    customData
                );

                // Check notification type and handle accordingly
                if (customData) {
                    const notificationType = customData.notificationType;

                    logger.info(
                        `📋 [DEBUG] Notification type: ${notificationType}`
                    );
                    logger.info(`📋 [DEBUG] Full customData:`, customData);

                    logger.info(
                        "📋 [DeliveryIssueToastProvider] Notification type detected:",
                        notificationType,
                        "| CustomData:",
                        customData
                    );

                    // Handle trip_created notifications (real-time updates)
                    if (notificationType === "trip_created") {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;

                        logger.info(
                            `🆕🆕🆕 [DeliveryIssueToastProvider] TRIP CREATED NOTIFICATION RECEIVED!`
                        );
                        logger.info(
                            `🆕 [DeliveryIssueToastProvider] Trip ID: ${tripId}`
                        );
                        logger.info(
                            `🆕 [DeliveryIssueToastProvider] Current user: ${currentUserEmail}`
                        );

                        // Invalidate trip list to show new trip across all browsers
                        logger.info(
                            `🔄 [DeliveryIssueToastProvider] Invalidating trip list cache...`
                        );
                        queryClient.invalidateQueries({
                            queryKey: ["trips", "list"], // Match actual query key pattern
                            refetchType: "active",
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["tripList"], // Legacy compatibility
                            refetchType: "active",
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["trips"], // Catch-all
                            refetchType: "active",
                        });

                        logger.info(
                            `✅✅✅ [DeliveryIssueToastProvider] Trip list cache invalidated for Trip ${tripId}`
                        );
                    } else if (notificationType === "trip_status_change") {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const oldStatus = customData.oldStatus || "";
                        const newStatus = customData.newStatus || "";

                        logger.info(
                            `🔄 [DeliveryIssueToastProvider] Trip status change: Trip ${tripId}: ${oldStatus} → ${newStatus}`
                        );

                        // 1. Trigger animation immediately (60 seconds)
                        triggerAnimation(tripId, 60000);

                        // 2. Invalidate queries to refetch authoritative data from server
                        // Don't do optimistic updates - they can corrupt cache with wrong filter keys
                        // Just invalidate and let React Query refetch with correct filters
                        queryClient.invalidateQueries({
                            queryKey: ["trips", "list"], // Match actual query key pattern
                            refetchType: "active",
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["tripList"], // Legacy compatibility
                            refetchType: "active",
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["trips"], // Catch-all for trip-related queries
                            refetchType: "active",
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["tripDetails", tripId],
                            refetchType: "active",
                        });
                        queryClient.invalidateQueries({
                            queryKey: ["trip", tripId],
                            refetchType: "active",
                        });

                        logger.info(
                            `✅ [DeliveryIssueToastProvider] Trip ${tripId} status updated and animated`
                        );
                    }
                    // Handle delivery_blocked notification (existing functionality)
                    else if (notificationType === "delivery_blocked") {
                        logger.info(
                            "🔥🔥🔥 [ENTRY] delivery_blocked handler STARTED"
                        );

                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const status = customData.status || "blocked";
                        const reasonText =
                            customData.reasonText || "Unknown reason";

                        logger.info(
                            `🔍🔍🔍 [DEBUG] delivery_blocked notification - TripId: ${tripId}, Status: ${status}, Reason: ${reasonText}`
                        );
                        logger.info(
                            "🔍 [DeliveryIssueToastProvider] delivery_blocked notification received:",
                            { tripId, status, reasonText }
                        );

                        // Determine if this is a critical issue (blocked or delayed)
                        const isCriticalIssue =
                            status === "delayed" || status === "blocked";

                        logger.info(
                            `🔍🔍🔍 [DEBUG] isCriticalIssue: ${isCriticalIssue} (status: ${status})`
                        );

                        if (isCriticalIssue) {
                            logger.info(
                                `🚨 [DEBUG] Showing BANNER for critical issue`
                            );
                            // Show full-width banner for critical issues
                            logger.info(
                                "🚨 [DeliveryIssueToastProvider] Critical issue detected, showing banner:",
                                { tripId, status, reasonText }
                            );

                            // Play notification bell sound using audio manager
                            // Audio manager handles browser autoplay policy
                            audioManager.playNotificationBell();

                            logger.info(
                                "🚨🚨🚨 [BEFORE addBanner] About to call addBanner with:",
                                { tripId, status, reasonText }
                            );

                            // Add to banner with proper formatting (label:value pattern)
                            addBanner({
                                tripId,
                                reason: reasonText,
                                messageHeader: "Critical Issue Reported",
                                messageContent: `Trip (${tripId}) needs immediate attention: <strong>Reason:</strong> ${reasonText}`,
                            });

                            logger.info(
                                "🚨🚨🚨 [AFTER addBanner] addBanner called successfully for trip:",
                                tripId
                            );
                            logger.info(
                                "🚨 [DeliveryIssueToastProvider] Banner shown for critical issue:",
                                { tripId, status, reasonText }
                            );

                            // Trigger animation for status change (60 seconds)
                            triggerAnimation(tripId, 60000);

                            // Invalidate queries to refetch trip list and show updated status
                            queryClient.invalidateQueries({
                                queryKey: ["trips", "list"],
                                refetchType: "active",
                            });
                            queryClient.invalidateQueries({
                                queryKey: ["tripList"], // Legacy compatibility
                                refetchType: "active",
                            });
                            queryClient.invalidateQueries({
                                queryKey: ["trips"], // Catch-all
                                refetchType: "active",
                            });

                            // Invalidate specific trip details
                            if (tripId) {
                                queryClient.invalidateQueries({
                                    queryKey: ["tripDetails", tripId],
                                    refetchType: "active",
                                });
                                queryClient.invalidateQueries({
                                    queryKey: ["trip", tripId],
                                    refetchType: "active",
                                });
                            }

                            logger.info(
                                `✅ [DeliveryIssueToastProvider] Cache invalidated and animation triggered for Trip ${tripId}`
                            );
                        } else {
                            logger.info(
                                `🔔 [DEBUG] Showing TOAST for non-critical issue (status: ${status})`
                            );
                            // Show toast for non-critical issues (cancelled, etc.)
                            const statusLabel =
                                status.charAt(0).toUpperCase() +
                                status.slice(1);
                            const messageContent =
                                notificationData.messageContent ||
                                `Trip has been ${status}`;

                            logger.info(
                                `🔔 [DEBUG] Toast details - Title: Trip #${tripId} ${statusLabel}, Message: ${messageContent}`
                            );

                            showNotificationToast({
                                title: `Trip ${tripId ? `#${tripId}` : ""} ${statusLabel}`,
                                message: messageContent,
                                variant:
                                    status === "cancelled"
                                        ? "danger"
                                        : "warning",
                                tripId,
                            });

                            logger.info(
                                "🔔 [DeliveryIssueToastProvider] Toast shown for non-critical issue:",
                                { tripId, status, message: messageContent }
                            );
                        }
                    }
                    // Handle trip_admin_block notification (Feature 26112)
                    else if (
                        notificationType === NotificationTypes.TRIP_ADMIN_BLOCK
                    ) {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const driverEmailsStr = customData.driverEmails || "";
                        const driverEmails = driverEmailsStr
                            .split(",")
                            .map((e: string) => e.trim().toLowerCase());
                        const isDriver = currentUserEmail
                            ? driverEmails.includes(
                                  currentUserEmail.toLowerCase()
                              )
                            : false;

                        logger.info(
                            `🚫 [DeliveryIssueToastProvider] TRIP ADMIN BLOCK notification - TripId: ${tripId}, IsDriver: ${isDriver}`
                        );

                        // Play notification bell sound
                        audioManager.playNotificationBell();

                        // Show banner for all users (drivers and admins)
                        addBanner({
                            tripId,
                            reason: "Trip Blocked",
                            messageHeader: NotificationMessages.HEADER_BLOCKED,
                            messageContent: isDriver
                                ? NotificationMessages.TRIP_BLOCKED_DRIVER(
                                      tripId
                                  )
                                : NotificationMessages.TRIP_BLOCKED_ADMIN(
                                      tripId
                                  ),
                        });

                        logger.info(
                            "✅ [DeliveryIssueToastProvider] Block notification shown:",
                            { tripId, isDriver }
                        );
                    }
                    // Handle trip_admin_unblock notification (Feature 26112)
                    else if (
                        notificationType ===
                        NotificationTypes.TRIP_ADMIN_UNBLOCK
                    ) {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const driverEmailsStr = customData.driverEmails || "";
                        const driverEmails = driverEmailsStr
                            .split(",")
                            .map((e: string) => e.trim().toLowerCase());
                        const isDriver = currentUserEmail
                            ? driverEmails.includes(
                                  currentUserEmail.toLowerCase()
                              )
                            : false;

                        logger.info(
                            `✅ [DeliveryIssueToastProvider] TRIP ADMIN UNBLOCK notification - TripId: ${tripId}, IsDriver: ${isDriver}`
                        );

                        // Play notification bell sound
                        audioManager.playNotificationBell();

                        // Show banner for all users (drivers and admins)
                        addBanner({
                            tripId,
                            reason: "Trip Unblocked",
                            messageHeader:
                                NotificationMessages.HEADER_UNBLOCKED,
                            messageContent: isDriver
                                ? NotificationMessages.TRIP_UNBLOCKED_DRIVER(
                                      tripId
                                  )
                                : NotificationMessages.TRIP_UNBLOCKED_ADMIN(
                                      tripId
                                  ),
                            variant: "success", // Green banner for unblock/resume
                        });

                        logger.info(
                            "✅ [DeliveryIssueToastProvider] Unblock notification shown:",
                            { tripId, isDriver }
                        );
                    }
                    // Handle trip_admin_cancel notification (Feature 26112)
                    else if (
                        notificationType === NotificationTypes.TRIP_ADMIN_CANCEL
                    ) {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const reasonText =
                            customData.reasonText || "No reason provided";

                        logger.info(
                            `❌ [DeliveryIssueToastProvider] TRIP ADMIN CANCEL notification - TripId: ${tripId}`
                        );
                        logger.info(
                            `❌ [DeliveryIssueToastProvider] Reason: ${reasonText}`
                        );

                        // Play notification bell sound
                        audioManager.playNotificationBell();

                        // Show GLOBAL BANNER to ALL USERS (admins + drivers)
                        addBanner({
                            tripId,
                            reason: reasonText,
                            messageHeader:
                                NotificationMessages.HEADER_CANCELLED,
                            messageContent:
                                NotificationMessages.TRIP_CANCELLED_ADMIN(
                                    tripId,
                                    reasonText
                                ),
                        });

                        logger.info(
                            "✅ [DeliveryIssueToastProvider] Cancel notification shown:",
                            { tripId, reasonText }
                        );
                    } else if (
                        notificationType ===
                        NotificationTypes.REQUEST_REMOVED_FROM_TRIP
                    ) {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const requestId = customData.requestId
                            ? parseInt(customData.requestId)
                            : 0;
                        const returnRequired =
                            customData.returnRequired === true ||
                            customData.returnRequired === "true";

                        logger.info(
                            `🔄 [DeliveryIssueToastProvider] REQUEST REMOVED notification - TripId: ${tripId}, RequestId: ${requestId}, ReturnRequired: ${returnRequired}`
                        );

                        audioManager.playNotificationBell();

                        addBanner({
                            tripId,
                            reason: "Route Updated",
                            messageHeader:
                                NotificationMessages.HEADER_REQUEST_REMOVED,
                            messageContent:
                                NotificationMessages.REQUEST_REMOVED_FROM_TRIP(
                                    tripId,
                                    requestId,
                                    returnRequired
                                ),
                            variant: "update",
                        });

                        logger.info(
                            "✅ [DeliveryIssueToastProvider] Request removal notification shown:",
                            { tripId, requestId, returnRequired }
                        );
                    }
                    // Handle trip_admin_delete notification (Feature 27406)
                    else if (
                        notificationType === NotificationTypes.TRIP_ADMIN_DELETE
                    ) {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const deletedBy =
                            customData.deletedBy || "Administrator";

                        logger.info(
                            `🗑️ [DeliveryIssueToastProvider] TRIP ADMIN DELETE notification - TripId: ${tripId}, DeletedBy: ${deletedBy}`
                        );

                        // Play notification bell sound
                        audioManager.playNotificationBell();

                        // Show GLOBAL BANNER to ALL USERS (admins + drivers)
                        addBanner({
                            tripId,
                            reason: "Trip Deleted",
                            messageHeader: NotificationMessages.HEADER_DELETED,
                            messageContent:
                                NotificationMessages.TRIP_DELETED_ADMIN(
                                    tripId,
                                    deletedBy
                                ),
                            showGoToTrip: false,
                        });

                        logger.info(
                            "✅ [DeliveryIssueToastProvider] Delete notification banner shown:",
                            { tripId, deletedBy }
                        );
                    }
                    // Handle trip_admin_delete notification (Feature 27406)
                    else if (
                        notificationType === NotificationTypes.TRIP_ADMIN_DELETE
                    ) {
                        const tripId = customData.tripId
                            ? parseInt(customData.tripId)
                            : 0;
                        const deletedBy =
                            customData.deletedBy || "Administrator";

                        logger.info(
                            `🗑️ [DeliveryIssueToastProvider] TRIP ADMIN DELETE notification - TripId: ${tripId}, DeletedBy: ${deletedBy}`
                        );

                        // Play notification bell sound
                        audioManager.playNotificationBell();

                        // Show GLOBAL BANNER to ALL USERS (admins + drivers)
                        addBanner({
                            tripId,
                            reason: "Trip Deleted",
                            messageHeader: NotificationMessages.HEADER_DELETED,
                            messageContent:
                                NotificationMessages.TRIP_DELETED_ADMIN(
                                    tripId,
                                    deletedBy
                                ),
                        });

                        logger.info(
                            "✅ [DeliveryIssueToastProvider] Delete notification banner shown:",
                            { tripId, deletedBy }
                        );
                    }
                }
            }
        },
        [
            showNotificationToast,
            addBanner,
            triggerAnimation,
            queryClient,
            currentUserEmail,
        ]
    );

    // Register handler with SignalR context
    // Re-register when handleNotification changes to avoid stale closures
    useEffect(() => {
        logger.info(
            "🍞🍞🍞 [DeliveryIssueToastProvider] useEffect RUNNING - About to register notification handler"
        );
        logger.info(
            "🍞 [DeliveryIssueToastProvider] registerNotificationHandler type:",
            typeof registerNotificationHandler
        );
        logger.info(
            "🍞 [DeliveryIssueToastProvider] handleNotification type:",
            typeof handleNotification
        );

        const unregister = registerNotificationHandler(
            "DeliveryIssueBannerHandler",
            handleNotification
        );

        logger.info(
            "🍞🍞🍞 [DeliveryIssueToastProvider] Handler registered successfully, unregister type:",
            typeof unregister
        );

        // Cleanup on unmount or when handler changes
        return () => {
            logger.info(
                "🍞 [DeliveryIssueToastProvider] Unregistering notification handler"
            );
            unregister();
        };
    }, [handleNotification, registerNotificationHandler]); // Re-register when handler changes

    return <>{children}</>;
}
